import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import AdminSidebar from '../components/AdminSidebar';
import AdminTopBar from '../components/AdminTopBar';

export default function AdminPriceIntelligence() {
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [heatmap, setHeatmap] = useState([]);
    const [alerts, setAlerts] = useState(null);
    const [realtimeMetrics, setRealtimeMetrics] = useState(null);
    const [realtimeMetricsError, setRealtimeMetricsError] = useState('');
    const [loading, setLoading] = useState(true);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
            const headers = { Authorization: `Bearer ${adminToken}` };

            const [overviewRes, heatmapRes, alertsRes, realtimeRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/admin/pricing/overview`, { headers }),
                fetch(`${API_BASE_URL}/api/admin/pricing/heatmap`, { headers }),
                fetch(`${API_BASE_URL}/api/admin/pricing/alerts`, { headers }),
                fetch(`${API_BASE_URL}/api/realtime/admin/realtime-metrics`, { headers })
            ]);

            const overviewData = await overviewRes.json();
            const heatmapData = await heatmapRes.json();
            const alertsData = await alertsRes.json();
            const realtimeData = await realtimeRes.json();

            setOverview(overviewData.stats || null);
            setHeatmap(heatmapData.heatmap || []);
            setAlerts(alertsData.alerts || null);
            if (realtimeRes.ok && realtimeData?.success) {
                setRealtimeMetrics(realtimeData.metrics || null);
                setRealtimeMetricsError('');
            } else {
                setRealtimeMetrics(null);
                setRealtimeMetricsError('Realtime KPI verisi alınamadı.');
            }
        } catch (err) {
            console.error('Failed to fetch price intelligence:', err);
            setRealtimeMetricsError('Realtime KPI verisi alınamadı.');
        } finally {
            setLoading(false);
        }
    };

    const getConfidenceColor = (confidence) => {
        if (confidence >= 0.8) return 'bg-green-500';
        if (confidence >= 0.5) return 'bg-blue-500';
        if (confidence >= 0.3) return 'bg-orange-500';
        return 'bg-red-500';
    };

    const getSourceBadge = (source) => {
        const badges = {
            USER_PURCHASE: { label: 'Kullanıcı', color: 'bg-green-100 text-green-800' },
            COMMUNITY_MEDIAN: { label: 'Topluluk', color: 'bg-blue-100 text-blue-800' },
            REFERENCE_SEED: { label: 'Referans', color: 'bg-orange-100 text-orange-800' },
            NONE: { label: 'Yok', color: 'bg-gray-100 text-gray-800' }
        };
        return badges[source] || badges.NONE;
    };

    const realtimeLatency = realtimeMetrics?.event_to_refetch_latency_ms || {};
    const realtimeCacheInvalidation = realtimeMetrics?.cache_invalidation_total || {};
    const realtimeLabelMetrics = realtimeMetrics?.price_label_experiment || {};
    const realtimeSloStatus = Number(realtimeLatency?.avgMs || 0) <= 1500 ? 'OK' : 'RISK';

    if (loading) return <div className="p-8">Yükleniyor...</div>;

    return (
        <div className="flex min-h-screen bg-slate-50 dark:bg-[#0d141c]">
            <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />

            <main className="flex-1 md:ml-[var(--admin-sidebar-width,18rem)] flex flex-col min-h-screen transition-[margin] duration-300">
                <AdminTopBar title="Fiyat İstihbaratı" onToggleMobile={() => setMobileSidebarOpen(true)} />

                <div className="p-4 sm:p-8 flex-1">
                    <header className="mb-6">
                        <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">Fiyat İstihbaratı</h1>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">Fiyatlandırma sistemi sağlık durumu ve analitik</p>
                    </header>

                    <div className="flex flex-wrap gap-4 border-b border-gray-200 dark:border-slate-700 mb-6 font-bold">
                        <button className="px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400">
                            Analiz Paneli
                        </button>
                        <button onClick={() => navigate('/admin/pricing/seeded')} className="px-4 py-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 transition-colors">
                            Seeded Veritabanı
                        </button>
                        <button onClick={() => navigate('/admin/pricing/mvp')} className="px-4 py-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 transition-colors">
                            MVP Operasyon
                        </button>
                        <button onClick={() => navigate('/admin/pricing/candidates')} className="px-4 py-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 transition-colors">
                            Candidate Havuzu
                        </button>
                        <button onClick={() => navigate('/admin/pricing/damping')} className="px-4 py-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 transition-colors">
                            Damping
                        </button>
                        <button onClick={() => navigate('/admin/pricing/unresolved')} className="px-4 py-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 transition-colors">
                            Tanımsızlar
                        </button>
                        <button onClick={() => navigate('/admin/pricing/market-reference')} className="px-4 py-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 transition-colors">
                            Piyasa Referansı
                        </button>
                    </div>

                    {overview && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                                <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Toplam Ürün</div>
                                <div className="text-3xl font-bold text-slate-800 dark:text-white">{overview.totalProducts}</div>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 shadow-sm border border-green-200 dark:border-green-800">
                                <div className="text-sm text-green-700 dark:text-green-400 mb-1">Kullanıcı Fiyatı</div>
                                <div className="text-3xl font-bold text-green-800 dark:text-green-300">{overview.sourceCounts.userPurchase}</div>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 shadow-sm border border-blue-200 dark:border-blue-800">
                                <div className="text-sm text-blue-700 dark:text-blue-400 mb-1">Topluluk Medianı</div>
                                <div className="text-3xl font-bold text-blue-800 dark:text-blue-300">{overview.sourceCounts.communityMedian}</div>
                            </div>
                            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-6 shadow-sm border border-orange-200 dark:border-orange-800">
                                <div className="text-sm text-orange-700 dark:text-orange-400 mb-1">Referans Seed</div>
                                <div className="text-3xl font-bold text-orange-800 dark:text-orange-300">{overview.sourceCounts.seedReference || overview.sourceCounts.referenceSeed || 0}</div>
                            </div>
                        </div>
                    )}

                    {alerts && (
                        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 mb-8">
                            <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">Uyarılar & Aksiyonlar</h2>
                            <div className="space-y-2">
                                {alerts.insufficientData.length > 0 && <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400"><span className="text-2xl">⚠️</span><span>{alerts.insufficientData.length} ürün yetersiz veri ({'<'}5 sepet)</span></div>}
                                {alerts.lowConfidence.length > 0 && <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400"><span className="text-2xl">⚠️</span><span>{alerts.lowConfidence.length} ürün düşük güven skoru ({'<'}0.3)</span></div>}
                                {alerts.noPrice.length > 0 && <div className="flex items-center gap-2 text-red-700 dark:text-red-400"><span className="text-2xl">❌</span><span>{alerts.noPrice.length} ürün fiyat yok</span></div>}
                                {alerts.insufficientData.length === 0 && alerts.lowConfidence.length === 0 && alerts.noPrice.length === 0 && <div className="flex items-center gap-2 text-green-700 dark:text-green-400"><span className="text-2xl">✅</span><span>Tüm ürünler sağlıklı</span></div>}
                            </div>
                        </div>
                    )}

                    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-slate-800 dark:text-white">SLO / KPI İzleme</h2>
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${realtimeSloStatus === 'OK' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                                Event->Refetch SLO: {realtimeSloStatus}
                            </span>
                        </div>
                        {realtimeMetricsError ? (
                            <div className="text-sm text-red-600 dark:text-red-300">{realtimeMetricsError}</div>
                        ) : null}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Event->Refetch Avg (ms)</div>
                                <div className="text-2xl font-bold text-slate-900 dark:text-white">{Number(realtimeLatency?.avgMs || 0).toFixed(1)}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Max: {Number(realtimeLatency?.maxMs || 0).toFixed(1)} | Count: {Number(realtimeLatency?.count || 0)}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Cache Invalidation</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white mt-1">Pricing: {Number(realtimeCacheInvalidation?.pricing || 0)}</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">Inventory: {Number(realtimeCacheInvalidation?.inventory || 0)}</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">Plan: {Number(realtimeCacheInvalidation?.plan || 0)}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Price Label KPI</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white mt-1">Impression (On): {Number(realtimeLabelMetrics?.impressions?.labels_on || 0)}</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">Impression (Off): {Number(realtimeLabelMetrics?.impressions?.labels_off || 0)}</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">Actions: {Number(realtimeLabelMetrics?.actions?.add_to_list || 0) + Number(realtimeLabelMetrics?.actions?.postpone || 0) + Number(realtimeLabelMetrics?.actions?.alternative_select || 0)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">Güven Haritası</h2>
                        <div className="grid grid-cols-6 md:grid-cols-10 lg:grid-cols-15 gap-2">
                            {heatmap.map((product) => {
                                const badge = getSourceBadge(product.source);
                                const label = product.code || product.name || 'PRD';
                                return (
                                    <div
                                        key={product.id || label}
                                        className={`${getConfidenceColor(product.confidence)} rounded p-2 text-white text-xs font-bold cursor-pointer hover:scale-110 transition-transform`}
                                        title={`${product.name}\n${(product.price || 0).toFixed(2)} ${product.unit}\nGüven: ${product.confidence}\nKaynak: ${badge.label}`}
                                    >
                                        {String(label).substring(0, 3)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
